import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Center, Flex, Group, Loader, Paper, Text, Title } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef, type MRT_PaginationState } from "mantine-react-table";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

const HISTORY_PAGE_SIZE = 20;

type CreditAdjustmentRow = {
  id: string;
  createdAt: string | Date;
  operation: "ADD" | "DEDUCT";
  credits: number;
  previousBalance: number;
  newBalance: number;
  reason: string;
  adminEmailSnapshot: string;
  adminNameSnapshot: string;
  targetEmailSnapshot: string;
  companyNameSnapshot: string | null;
};

export default function AdminCreditAdjustmentHistoryPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CreditAdjustmentRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: HISTORY_PAGE_SIZE,
  });
  const previousParamsRef = useRef<string>("");

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.creditAdjustmentHistory.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });
      setRows(
        res.rows.map((row: CreditAdjustmentRow) => ({
          ...row,
          createdAt: row.createdAt instanceof Date ? row.createdAt : new Date(String(row.createdAt)),
        }))
      );
      setTotalRows(res.meta.total);
    } catch (err) {
      handleTrpcError(err);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const paramsString = JSON.stringify({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });
    if (paramsString === previousParamsRef.current) {
      return;
    }
    previousParamsRef.current = paramsString;
    void fetchHistory();
  }, [fetchHistory, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo<MRT_ColumnDef<CreditAdjustmentRow>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "Date",
        Cell: ({ cell }) => {
          const d = cell.getValue<Date | string>();
          const value = d instanceof Date ? d : new Date(d);
          return <Text size="sm">{value.toLocaleString()}</Text>;
        },
      },
      {
        accessorKey: "operation",
        header: "Type",
        Cell: ({ cell }) => {
          const operation = cell.getValue<"ADD" | "DEDUCT">();
          return (
            <Badge color={operation === "ADD" ? "teal" : "red"} variant="light">
              {operation}
            </Badge>
          );
        },
      },
      { accessorKey: "credits", header: "Credits" },
      { accessorKey: "previousBalance", header: "Before" },
      { accessorKey: "newBalance", header: "After" },
      { accessorKey: "targetEmailSnapshot", header: "User" },
      { accessorKey: "companyNameSnapshot", header: "Company" },
      { accessorKey: "adminEmailSnapshot", header: "Admin" },
      { accessorKey: "reason", header: "Reason" },
    ],
    []
  );

  return (
    <Flex direction="column" p="lg" gap="lg" mih="100%">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>Credit adjustment history</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Persistent log of all admin credit adds/deductions. These records remain available even if user data is
            deleted later.
          </Text>
        </div>
        <Button variant="default" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate("/admin/users")}>
          Back to users
        </Button>
      </Group>

      <Paper p="md" withBorder>
        <MantineReactTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          state={{ isLoading: loading, pagination }}
          onPaginationChange={setPagination}
          manualPagination
          rowCount={totalRows}
          enableGlobalFilter={false}
          enableColumnFilters={false}
          mantineTableContainerProps={{ style: { minHeight: 360 } }}
        />
        {loading && rows.length === 0 && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
      </Paper>
    </Flex>
  );
}
